import React from 'react';
import { Link } from 'react-router-dom';
import '../../styles/components/ErrorBoundary.css';

/**
 * Error Boundary component for graceful error handling
 * Catches JavaScript errors in child components and displays a fallback UI
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state to show fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to console (could be sent to error reporting service)
        this.setState({ errorInfo });

        // In production, you could send this to an error reporting service
        if (process.env.NODE_ENV === 'production') {
            // Example: logErrorToService(error, errorInfo);
        }
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="error-boundary">
                    <div className="error-boundary-content">
                        <div className="error-icon">⚠️</div>
                        <h1 className="error-title">Oops! Something went wrong</h1>
                        <p className="error-message">
                            We're sorry, but something unexpected happened. Please try refreshing the page.
                        </p>
                        <div className="error-actions">
                            <button onClick={this.handleReset} className="error-btn retry-btn">
                                Try Again
                            </button>
                            <Link to="/" className="error-btn home-btn" onClick={this.handleReset}>
                                Go to Home
                            </Link>
                        </div>
                        {process.env.NODE_ENV !== 'production' && this.state.error && (
                            <details className="error-details">
                                <summary>Error Details (Development Only)</summary>
                                <pre>{this.state.error.toString()}</pre>
                                {this.state.errorInfo && (
                                    <pre>{this.state.errorInfo.componentStack}</pre>
                                )}
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
